import React, { useState, useEffect, useCallback, useRef } from 'react';
import styles from '../styles/inventoryModal.module.css';
import { apiService } from '../services/api';
import { InventoryService } from '../services/inventoryService';
import { EncryptionService } from '../services/encryptionService';
import { useDialog } from '../context/DialogContext';
import { useShare } from '../context/ShareContext';

interface InventoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  user?: any;
  onInventoryUpdate?: (inventoryType: string) => void;
  currentVersions?: Record<string, string>;
  inventoryStatuses?: any[];
  initialTab?: string;
}

const INVENTORY_TYPES = [
  { key: 'resentments', label: 'Resentments', myLabel: 'My Resentments' },
  { key: 'fears', label: 'Fears', myLabel: 'My Fears' },
  { key: 'harms', label: 'Harms Done', myLabel: 'My Harms Done' },
];

const IMPACT_OPTIONS = [
  { value: 'self-esteem', label: 'Self-esteem' },
  { value: 'pride', label: 'Pride' },
  { value: 'pocketbook', label: 'Pocketbook' },
  { value: 'personal-relationships', label: 'Personal Relationships' },
  { value: 'sexual-relationships', label: 'Sexual Relationships' },
  { value: 'emotional-security', label: 'Emotional Security' },
  { value: 'ambitions', label: 'Ambitions' },
];

const DEFECT_OPTIONS = [
  { value: 'controlling', label: 'Controlling' },
  { value: 'dishonest', label: 'Dishonest' },
  { value: 'disengaged', label: 'Disengaged' },
  { value: 'entitled', label: 'Entitled' },
  { value: 'frightened', label: 'Frightened' },
  { value: 'greedy', label: 'Greedy' },
  { value: 'inconsiderate', label: 'Inconsiderate' },
  { value: 'impulsive', label: 'Impulsive' },
  { value: 'lustful', label: 'Lustful' },
  { value: 'self-pity', label: 'Self-pity' },
  { value: 'selfish', label: 'Selfish' },
  { value: 'willful', label: 'Willful' },
];

type InventoryData = Record<string, any[]>;

function getDefaultItem(type: string): any {
  switch (type) {
    case 'resentments':
      return { who: '', why: '', feeling: '', impacts: [], myPart: '' };
    case 'fears':
      return { fear: '', why: '', impacts: [] };
    case 'harms':
      return { whoHarmed: '', how: '', defects: [], alternative: '' };
    default:
      return {};
  }
}

function isComplete(type: string, item: any): boolean {
  if (!item) return false;
  
  if (type === 'resentments') {
    return item.who && item.why && item.feeling && Array.isArray(item.impacts) && item.impacts.length > 0 && item.myPart;
  }
  if (type === 'fears') {
    return item.fear && item.why && Array.isArray(item.impacts) && item.impacts.length > 0;
  }
  if (type === 'harms') {
    return item.whoHarmed && item.how && Array.isArray(item.defects) && item.defects.length > 0 && item.alternative;
  }
  return false;
}



const InventoryModal: React.FC<InventoryModalProps> = ({ isOpen, onClose, user, onInventoryUpdate, currentVersions, inventoryStatuses, initialTab }) => {
  const { showConfirm: showConfirmDialog, showAlert, showHelp: showHelpDialog } = useDialog();
  const { openShareModal } = useShare();
  const [currentType, setCurrentType] = useState(initialTab || 'resentments');
  const [data, setData] = useState<InventoryData>({});
  const [entryInput, setEntryInput] = useState('');
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [form, setForm] = useState<any>(null);

  const [importStatus, setImportStatus] = useState('');
  const [completionStatus, setCompletionStatus] = useState<Record<string, boolean>>({});
  const completionStatusLoadedRef = useRef(false);
  
  // Auto-save state management
  const [savingEntry, setSavingEntry] = useState<number | null>(null);
  const [savedEntry, setSavedEntry] = useState<number | null>(null);
  const [saveError, setSaveError] = useState<number | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const authErrorShownRef = useRef(false); // Track if we've shown auth error to avoid spam
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const periodicSaveIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Update currentType when initialTab changes (e.g., when modal opens with different tab)
  useEffect(() => {
    if (isOpen && initialTab) {
      setCurrentType(initialTab);
    }
  }, [isOpen, initialTab]);

  // Reset auth error flag when modal opens or user changes
  useEffect(() => {
    if (isOpen) {
      authErrorShownRef.current = false;
    }
  }, [isOpen, user]);

  // Helper function to get the current version name for each inventory type
  const getCurrentVersionName = useCallback(() => {
    if (currentVersions && currentVersions[currentType]) {
      return currentVersions[currentType];
    }
    // Fallback to the old naming convention if no currentVersions provided
    return `${currentType}-current`;
  }, [currentType, currentVersions]);

  // Use inventory statuses from props instead of loading from API
  useEffect(() => {
    if (!inventoryStatuses || !isOpen) return;
    
    // Convert inventory statuses to the data format expected by the modal
    const allData: InventoryData = {};
    
    for (const status of inventoryStatuses) {
      // For now, we'll still need to load the actual items from API
      // but we can use the status to determine if there are items
      if (status.total > 0) {
        // Load the actual items for this type
        const loadItems = async () => {
          try {
            const versionName = currentVersions && currentVersions[status.type] 
              ? currentVersions[status.type] 
              : `${status.type}-current`;
            const items = await InventoryService.getInventory(status.type, versionName);
            const filteredItems = items.filter(item => item._type !== 'completion_status');
            allData[status.type] = filteredItems;
            setData(prev => ({ ...prev, [status.type]: filteredItems }));
          } catch (error) {
            console.error(`Error loading ${status.type} data:`, error);
            allData[status.type] = [];
            setData(prev => ({ ...prev, [status.type]: [] }));
          }
        };
        loadItems();
      } else {
        allData[status.type] = [];
        setData(prev => ({ ...prev, [status.type]: [] }));
      }
    }
  }, [inventoryStatuses, isOpen, currentVersions]);

  // Load completion status from API
  useEffect(() => {
    const loadCompletionStatus = async () => {
      if (!user || Object.keys(data).length === 0) return;
      

      
      try {
        const status: Record<string, boolean> = {};
        
        for (const type of INVENTORY_TYPES) {
          try {
            const versionName = currentVersions && currentVersions[type.key] 
              ? currentVersions[type.key] 
              : `${type.key}-current`;
            const completionData = await InventoryService.getInventoryCompletion(type.key, versionName);
            status[type.key] = completionData.completed || false;
    
          } catch (error) {
            console.error(`Error loading completion status for ${type.key}:`, error);
            status[type.key] = false;
          }
        }
        

        setCompletionStatus(status);
        completionStatusLoadedRef.current = true;
      } catch (error) {
        console.error('Error loading completion status:', error);
      }
    };

    loadCompletionStatus();
  }, [user, data, currentVersions]);

  const getCurrentData = () => {
    const currentData = data[currentType] || [];
    // Filter out completion status items when getting current data
    return currentData.filter(item => item._type !== 'completion_status');
  };

  // Immediate save function (bypasses debounce) - used for critical operations
  const immediateSaveEntry = useCallback(async (idx: number, formData: any): Promise<boolean> => {
    const versionName = getCurrentVersionName();
    const currentData = data[currentType] || [];
    
    // Check if data actually changed
    const hasChanges = JSON.stringify(formData) !== JSON.stringify(currentData[idx]);
    if (!hasChanges) return true;

    try {
      const newData = [...currentData];
      newData[idx] = formData;

      await InventoryService.saveInventory(currentType, versionName, newData);

      // Update local state
      setData(prev => ({
        ...prev,
        [currentType]: newData
      }));

      // Clear unsaved changes flag
      setHasUnsavedChanges(false);

      // Notify parent to update counters
      if (onInventoryUpdate) {
        onInventoryUpdate(currentType);
      }

      return true;
    } catch (error: any) {
      console.error('Error saving entry:', error);
      
      // Check if it's an authentication error
      const errorMessage = error?.message || String(error);
      const isAuthError = errorMessage.includes('401') || 
                         errorMessage.includes('Not logged in') ||
                         errorMessage.includes('Token expired') ||
                         errorMessage.includes('Invalid token') ||
                         errorMessage.includes('No authentication token');
      
      if (isAuthError) {
        showAlert({
          title: 'Session Expired',
          message: 'Your session has expired. Please log out and log back in to continue saving changes.',
          okText: 'OK'
        });
      }
      
      return false;
    }
  }, [currentType, data, getCurrentVersionName, onInventoryUpdate]);

  // Auto-save function with debouncing
  const autoSaveEntry = useCallback(async (idx: number, formData: any) => {
    // Mark as having unsaved changes
    setHasUnsavedChanges(true);

    // Clear any existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set debounce timeout (300ms)
    saveTimeoutRef.current = setTimeout(async () => {
      const versionName = getCurrentVersionName();
      const currentData = data[currentType] || [];
      
      // Check if data actually changed (compare to raw data, not filtered)
      const hasChanges = JSON.stringify(formData) !== JSON.stringify(currentData[idx]);
      if (!hasChanges) {
        setHasUnsavedChanges(false);
        return;
      }

      // Set saving state
      setSavingEntry(idx);
      setSaveError(null);
      setSavedEntry(null);

      try {
        const newData = [...currentData];
        newData[idx] = formData;

        await InventoryService.saveInventory(currentType, versionName, newData);

        // Update local state
        setData(prev => ({
          ...prev,
          [currentType]: newData
        }));

        // Show success feedback
        setSavingEntry(null);
        setSavedEntry(idx);
        setTimeout(() => setSavedEntry(null), 1000);

        // Clear unsaved changes flag
        setHasUnsavedChanges(false);

        // Notify parent to update counters
        if (onInventoryUpdate) {
          onInventoryUpdate(currentType);
        }

        // Don't reload from API - local state is already updated
        // Reloading causes race condition where old data overwrites new data
      } catch (error: any) {
        console.error('Error auto-saving entry:', error);
        setSavingEntry(null);
        setSaveError(idx);
        
        // Check if it's an authentication error
        const errorMessage = error?.message || String(error);
        const isAuthError = errorMessage.includes('401') || 
                           errorMessage.includes('Not logged in') ||
                           errorMessage.includes('Token expired') ||
                           errorMessage.includes('Invalid token') ||
                           errorMessage.includes('No authentication token');
        
        if (isAuthError && !authErrorShownRef.current) {
          // Show auth error once per session to avoid spam
          authErrorShownRef.current = true;
          showAlert({
            title: 'Session Expired',
            message: 'Your session has expired. Please log out and log back in to continue saving changes.',
            okText: 'OK'
          });
        }
      }
    }, 300);
  }, [currentType, data, getCurrentVersionName, onInventoryUpdate]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      if (periodicSaveIntervalRef.current) {
        clearInterval(periodicSaveIntervalRef.current);
      }
    };
  }, []);

  // Periodic auto-save (every 10 seconds when entry is expanded)
  useEffect(() => {
    // Clear any existing interval
    if (periodicSaveIntervalRef.current) {
      clearInterval(periodicSaveIntervalRef.current);
    }

    // Only start periodic save if we have an expanded entry
    if (expandedIndex !== null && form && user) {
      periodicSaveIntervalRef.current = setInterval(() => {
        // autoSaveEntry already has dirty checking, so no need to duplicate
        autoSaveEntry(expandedIndex, form);
      }, 10000); // 10 seconds
    }

    return () => {
      if (periodicSaveIntervalRef.current) {
        clearInterval(periodicSaveIntervalRef.current);
      }
    };
  }, [expandedIndex, form, user, autoSaveEntry]);

  // Save on visibility change (when user switches browser tabs)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && expandedIndex !== null && form) {
        // User switched to another browser tab - save their work
        autoSaveEntry(expandedIndex, form);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [expandedIndex, form, autoSaveEntry]);

  const getInventoryCounts = () => {
    const items = getCurrentData();
    const total = items.length;
    const complete = items.filter((item: any) => isComplete(currentType, item)).length;
    return { total, complete };
  };

  const handleTab = async (type: string) => {
    // Save current entry before switching tabs using immediate save
    if (expandedIndex !== null && form) {
      // Clear any pending debounced save
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      
      const saved = await immediateSaveEntry(expandedIndex, form);
      if (!saved) {
        // Show error but still allow tab switch - user might want to abandon changes
        console.warn('Failed to save changes before tab switch');
      }
    }
    
    setCurrentType(type);
    setExpandedIndex(null);
    setEditingIndex(null);
    setForm(null);
  };

  // Handle modal close - save before closing
  const handleClose = async () => {
    // Clear any pending debounced save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // If there are unsaved changes, try to save immediately
    if (expandedIndex !== null && form) {
      const versionName = getCurrentVersionName();
      const currentData = data[currentType] || [];
      const hasChanges = JSON.stringify(form) !== JSON.stringify(currentData[expandedIndex]);
      
      if (hasChanges) {
        // Show saving indicator
        setSavingEntry(expandedIndex);
        
        const saved = await immediateSaveEntry(expandedIndex, form);
        
        if (!saved) {
          // Save failed - warn user
          setSavingEntry(null);
          showConfirmDialog({
            message: 'Failed to save your changes. If you close now, you will lose your recent work. Do you want to try again or close anyway?',
            onConfirm: () => {
              // User chose to close anyway
              setHasUnsavedChanges(false);
              onClose();
            },
            confirmText: 'Close Anyway',
            cancelText: 'Try Again',
            variant: 'danger'
          });
          return;
        }
        
        setSavingEntry(null);
      }
    }
    
    // All saved, safe to close
    setHasUnsavedChanges(false);
    onClose();
  };

  const handleEntryAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!entryInput.trim()) return;

    // Check authentication before proceeding
    if (!user) {
      showAlert({
        title: 'Not Logged In',
        message: 'Please log in to save inventory entries.',
        okText: 'OK'
      });
      return;
    }

    const newItem = getDefaultItem(currentType);
    if (currentType === 'resentments') {
      newItem.who = entryInput.trim();
    } else if (currentType === 'fears') {
      newItem.fear = entryInput.trim();
    } else if (currentType === 'harms') {
      newItem.whoHarmed = entryInput.trim();
    }

    const currentData = getCurrentData();
    const newData = [...currentData, newItem];
    const versionName = getCurrentVersionName();
    
    // Store the input value before clearing it
    const inputValue = entryInput.trim();
    
    // Optimistically update UI
    setData(prev => ({
      ...prev,
      [currentType]: newData
    }));
    setEntryInput('');

    // Save to API with proper error handling
    try {
      // Use InventoryService which handles encryption properly
      await InventoryService.saveInventory(currentType, versionName, newData);
      
      // Notify parent to update counters
      if (onInventoryUpdate) {
        onInventoryUpdate(currentType);
      }
      
      // Don't reload from API here - local state is already updated
      // Reloading immediately causes a race condition where old data is fetched
      // before the save completes, making the entry disappear
    } catch (error: any) {
      console.error('Error saving to API:', error);
      
      // Rollback the state change - remove the entry we just added
      setData(prev => ({
        ...prev,
        [currentType]: currentData
      }));
      
      // Restore the input value
      setEntryInput(inputValue);
      
      // Check if it's an authentication error
      const errorMessage = error?.message || String(error);
      const isAuthError = errorMessage.includes('401') || 
                         errorMessage.includes('Not logged in') ||
                         errorMessage.includes('Token expired') ||
                         errorMessage.includes('Invalid token') ||
                         errorMessage.includes('No authentication token');
      
      if (isAuthError) {
        showAlert({
          title: 'Session Expired',
          message: 'Your session has expired. Please log out and log back in to continue saving entries.',
          okText: 'OK'
        });
      } else if (errorMessage.includes('Failed to encrypt')) {
        showAlert({
          title: 'Encryption Error',
          message: 'Unable to encrypt your data. Please try refreshing the page. If the problem persists, try logging out and back in.',
          okText: 'OK'
        });
      } else if (errorMessage.includes('Network') || errorMessage.includes('fetch')) {
        showAlert({
          title: 'Network Error',
          message: 'Unable to save. Please check your internet connection and try again.',
          okText: 'OK'
        });
      } else {
        showAlert({
          title: 'Save Failed',
          message: 'Unable to save this entry. Please try again. If the problem persists, try refreshing the page.',
          okText: 'OK'
        });
      }
    }
  };

    const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (!form) return;
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>, group: 'impacts' | 'defects') => {
    if (!form) return;
    const value = e.target.value;
    const currentArray = form[group] || [];
    
    if (e.target.checked) {
      setForm({ ...form, [group]: [...currentArray, value] });
    } else {
      setForm({ ...form, [group]: currentArray.filter((v: string) => v !== value) });
    }
  };

  // Handle field blur - no-op (form state is enough for visual feedback)
  const handleFieldBlur = () => {
    // Do nothing here - form state already updated by handleFormChange
    // This preserves dirty checking by NOT updating data state prematurely
    // Actual saves happen on: periodic (10s), collapse, close, visibility change
  };


  const handleDelete = (idx: number) => {
    const currentData = getCurrentData();
    const item = currentData[idx];
    
    // Get the first field value based on inventory type
    let firstFieldValue = '';
    
    if (currentType === 'resentments') {
      firstFieldValue = item.who || 'this entry';
    } else if (currentType === 'fears') {
      firstFieldValue = item.fear || 'this entry';
    } else if (currentType === 'harms') {
      firstFieldValue = item.whoHarmed || 'this entry';
    }
    
    const confirmMessage = `Are you sure you want to delete '${firstFieldValue}'?`;
    
    showConfirmDialog({
      message: confirmMessage,
      onConfirm: async () => {
        // If deleting the currently expanded entry, save any other changes first
        if (expandedIndex === idx && form) {
          if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
          }
        }
        
        const newData = currentData.filter((_: any, i: number) => i !== idx);
        const versionName = getCurrentVersionName();
        
        setData(prev => ({
          ...prev,
          [currentType]: newData
        }));

        // Save to API
        if (user) {
          try {
            await InventoryService.saveInventory(currentType, versionName, newData);
            
            // If all items are deleted, clear the completion status
            if (newData.length === 0) {
              await InventoryService.saveInventoryCompletion(currentType, versionName, false);
            }
            
            // Notify parent to update counters
            if (onInventoryUpdate) {
              onInventoryUpdate(currentType);
            }
            
            // Don't reload from API - local state is already updated
            // Reloading causes race condition
          } catch (error) {
            console.error('Error saving to API:', error);
          }
        }
      },
      variant: 'danger'
    });
  };

  const handleExpand = async (idx: number) => {
    // If we're expanding a different entry, save the current one first using immediate save
    if (expandedIndex !== null && expandedIndex !== idx && form) {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      await immediateSaveEntry(expandedIndex, form);
    }
    
    // Toggle expansion
    if (expandedIndex === idx) {
      // Collapsing - save before collapsing using immediate save to ensure data is preserved
      if (form) {
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }
        const saved = await immediateSaveEntry(idx, form);
        if (!saved) {
          // Show error but still collapse - user can expand again if needed
          showAlert({
            message: 'Warning: Failed to save your changes. Please try editing again or check your connection.',
            variant: 'warning'
          });
        }
      }
      setExpandedIndex(null);
      setEditingIndex(null);
      setForm(null);
    } else {
      // Expanding - show edit form directly
      const currentData = getCurrentData();
      setExpandedIndex(idx);
      setEditingIndex(idx);
      setForm({ ...currentData[idx] });
    }
  };

  const handleStartOver = () => {
    const currentData = getCurrentData();
    const inventoryTypeLabel = INVENTORY_TYPES.find(t => t.key === currentType)?.label || currentType;
    
    // If inventory is empty or doesn't exist, just start over without confirmation
    if (!currentData || currentData.length === 0) {
      // Clear the current inventory data
      setData(prev => {
        const newData = { ...prev };
        delete newData[currentType];
        return newData;
      });

      // Clear completion status even if inventory is empty
      if (user) {
        try {
          const versionName = getCurrentVersionName();
          InventoryService.saveInventoryCompletion(currentType, versionName, false);
          
          // Notify parent to update counters
          if (onInventoryUpdate) {
            onInventoryUpdate(currentType);
          }
          
          // Don't reload from API - local state is already updated
        } catch (error) {
          console.error('Error clearing completion status:', error);
        }
      }
      return;
    }
    
    // If inventory has content, show confirmation
    showConfirmDialog({
      message: `Are you sure you want to clear your ${inventoryTypeLabel} inventory? All your entries will be permanently deleted. Make sure you've downloaded your current inventory if you want to keep a copy.`,
      onConfirm: async () => {
        const versionName = getCurrentVersionName();
        
        // Clear the current inventory data
        setData(prev => {
          const newData = { ...prev };
          delete newData[currentType];
          return newData;
        });

        // Delete from API
        if (user) {
          try {
            await InventoryService.deleteInventory(currentType, versionName);
            // Also clear the completion status since all items are deleted
            await InventoryService.saveInventoryCompletion(currentType, versionName, false);
            
            // Notify parent to update counters
            if (onInventoryUpdate) {
              onInventoryUpdate(currentType);
            }
            
            // Don't reload from API - local state is already updated
          } catch (error) {
            console.error('Error deleting from API:', error);
          }
        }
      },
      variant: 'danger'
    });
  };





  const openHelp = (fieldId: string) => {
    // Comprehensive help content for the modal
    const helpTexts: Record<string, string> = {
      'inventory': `
        <h2>🧾 How to Complete Your Step 4 Inventories</h2>
        <p>This tool helps you work through the <strong>three Step 4 inventories</strong>:</p>
        <ul>
            <li><strong>Resentments</strong></li>
            <li><strong>Fears</strong></li>
            <li><strong>Harms Done to Others</strong></li>
        </ul>
        <p>Use the <strong>tabs at the top</strong> to switch between them. As you make progress, each tab will show your completion status.</p>

        <h3>🪪 Getting Started</h3>
        <p>For each inventory:</p>
        <ol>
            <li><strong>Add entries quickly</strong> — enter names (or fears) one at a time and press <strong>Return</strong> or click <strong>"Add to List."</strong></li>
            <li><strong>Click any entry to add details</strong> — the entry expands to show editable fields. Fill them out and tab through.</li>
            <li><strong>Changes save automatically</strong> — when you leave a field or collapse an entry, your work is saved.</li>
        </ol>
        <p>When you've filled out all details for an entry, you'll see a ✅ check mark next to it. Incomplete entries will say <em>"click to see/add details."</em></p>
        <p>Once you've finished all entries for an inventory, check the <strong>"Completed"</strong> box at the top — the tab will update to show it's complete.</p>
        <blockquote>
            💡 When all three inventories are complete, you can mark this action as completed. That's a big accomplishment — completing these inventories is one of the hardest parts of working the 12 steps!
        </blockquote>

        <h3>❓ Need Help?</h3>
        <p>Not sure what a field means?<br>
        Click the <strong>blue question mark (?)</strong> next to it for a quick explanation.</p>
        <p>Your answers are saved automatically as you work, so you can close the inventory at any time and come back later.</p>

        <hr>

        <h3>📂 Manage This Inventory</h3>
        <p>Each inventory tab has three buttons at the top:</p>
        <ul>
            <li><strong>Download</strong> – Save an <strong>encrypted backup file</strong> (.json) of this inventory to your device. You'll choose where to save it and can rename the file if desired.</li>
            <li><strong>Upload</strong> – Restore a previously downloaded encrypted inventory file. Only works with files you've downloaded from Recovery Commons.</li>
            <li><strong>Start Over</strong> – Permanently delete all entries from this inventory. You'll be asked to confirm first.</li>
        </ul>
        <blockquote>
            🔐 <strong>Security & Privacy:</strong> Downloaded files are encrypted with your personal encryption key. Only you can decrypt and restore them. Your work is safe both in the cloud and on your device.
        </blockquote>
        <p><em>Note: These buttons only affect the inventory tab you're currently viewing — your other inventories remain unchanged.</em></p>

        <hr>

        <h3>📤 Share a PDF of Your Work</h3>
        <p>Want to share your Step 4 work with your sponsor or prepare for Step 5?</p>
        <p>Click <strong>"Share"</strong> from the menu in the upper-right corner. There you can generate a <strong>PDF of all your inventories</strong> — great for reviewing with your sponsor.</p>
      `,
      'resentment-who': `
        <h3>Who do I resent?</h3>
        <p>In this part of my Step Four work, I simply list <strong>who or what I resent</strong>.</p>
        <p>I don't analyze or justify—just put down the names or things.</p>
        <p>I think broadly:</p>
        <ul>
            <li><strong>People:</strong> family, partners, exes, friends, myself, bosses, teachers, police, clergy, sponsors.</li>
            <li><strong>Institutions:</strong> marriage, church, government, legal or mental health systems.</li>
            <li><strong>Things or situations:</strong> porn, social media, alcohol, money, my job, even my phone.</li>
        </ul>
        <p>This is just the first step of my resentment inventory. Later, I'll look at <strong>why I resent them</strong>, <strong>how it affected me</strong>, and <strong>my own part</strong>.</p>
        <p>I stay honest and fearless—this is for my healing, and I'll work through the details in the next steps.</p>
      `,
      'resentment-why': `
        <h3>Why do I resent them?</h3>
        <p>In this column, I briefly describe the specific experience or behavior that led to my resentment. I just stick to the facts—no need to overanalyze or dive into how it affected me emotionally.</p>
        <p><strong>What I do:</strong></p>
        <ul>
            <li>I write a clear, factual statement of what they did or what happened.</li>
            <li>I keep it short—like a bullet point summary.</li>
        </ul>
        <p><strong>Examples:</strong></p>
        <ul>
            <li>"They kept interrupting me during work meetings."</li>
            <li>"The clinic canceled my appointment two weeks in a row."</li>
            <li>"My ex-partner posted my private messages online."</li>
        </ul>
        <p>That's it—straightforward. This step helps me focus on exactly why the resentment exists, laying the groundwork to explore it more deeply later.</p>
      `,
      'resentment-feeling': `
        <h3>How did that make me feel?</h3>
        <p>In this field, I briefly name the emotion(s) I experience or experienced tied to this resentment. I focus on what I felt internally—whether it was anger, hurt, betrayal, disappointment, etc. This is about capturing my emotional reaction.</p>
        <p><strong>How I do it:</strong></p>
        <ul>
            <li>I use one or two emotion words—straightforward and honest.</li>
            <li>I keep it short—just enough to convey my true feeling.</li>
        </ul>
        <p><strong>Examples:</strong></p>
        <ul>
            <li>"I felt angry and dismissed."</li>
            <li>"embarrassed, hurt"</li>
            <li>"feel betrayed"</li>
        </ul>
      `,
      'resentment-impact': `
        <h3>What was lost, taken, or threatened inside me?</h3>
        <p>I check one or more areas of myself that were impacted by this resentment. These are about my <strong>internal parts, feelings, or needs—not external losses.</strong></p>
        <ul>
            <li><strong>Self‑esteem:</strong> My core sense of self-worth or value as a person.</li>
            <li><strong>Pride:</strong> How I believe others see me—my reputation or standing.</li>
            <li><strong>Pocketbook:</strong> My financial or material losses—money, property, possessions.</li>
            <li><strong>Personal Relationships:</strong> My connections with others—friendships, family ties, trust.</li>
            <li><strong>Sexual Relationships:</strong> My intimate or physical connections—needs, trust, closeness.</li>
            <li><strong>Emotional Security:</strong> My inner sense of stability—feeling safe, calm, or emotionally supported.</li>
            <li><strong>Ambitions:</strong> My goals, hopes, and dreams—career, personal growth, or future plans.</li>
        </ul>
      `,
      'resentment-part': `
        <h3>What was my part in this resentment?</h3>
        <p>I honestly examine how my own attitudes or actions contributed to or prolonged this resentment. This isn't about blaming myself—it's about accepting responsibility and finding opportunities for growth.</p>
        <p><strong>I ask myself:</strong></p>
        <ul>
            <li>Was I being selfish or self-willed?</li>
            <li>Was I dishonest with myself or others?</li>
            <li>Was I fearful, defensive, or trying to control the situation?</li>
            <li>Did I gossip, shun, or refuse to communicate?</li>
            <li>Could I have handled things differently?</li>
        </ul>
        <p><strong>Examples of owning my part:</strong></p>
        <ul>
            <li>"I didn't speak up when they crossed my boundary—I held it in and then stewed in anger."</li>
            <li>"I blamed them publicly and gossiped instead of talking privately."</li>
            <li>"I refused to admit I was afraid of being rejected, so I lashed out first."</li>
        </ul>
        <p>Being honest here helps me see the role I played—so I can learn and let go. It's a powerful step toward healing.</p>
      `,
      'fear-what': `
        <h3>What do I fear?</h3>
        <p>A <strong>Step Four Inventory</strong>—also called a <em>moral inventory</em>—is a <strong>written (or typed) self-examination</strong> used in Sex Addicts Anonymous (SAA) to honestly explore the thoughts, feelings, and behaviors that drive addiction. It covers key areas like resentments, fears, and harm done others.</p>
        <p>In this part of my Step Four work, I simply list <strong>what I fear</strong>.</p>
        <p>I don't analyze or justify—just put down the fears.</p>
        <p>I think broadly:</p>
        <ul>
            <li><strong>People:</strong> fear of rejection, fear of abandonment, fear of authority.</li>
            <li><strong>Situations:</strong> fear of failure, fear of success, fear of change.</li>
            <li><strong>Feelings:</strong> fear of emotions, fear of intimacy, fear of being alone.</li>
        </ul>
        <p>This is just the first step of my fear inventory. Later, I'll look at <strong>why I fear them</strong>, <strong>how it affected me</strong>, and <strong>my own part</strong>.</p>
        <p>I stay honest and fearless—this is for my healing, and I'll work through the details in the next steps.</p>
      `,
      'fear-why': `
        <h3>Why do I have this fear?</h3>
        <p>In this field, I dig into the real reason behind my fear—what belief or worry is driving it. I look for the story my mind is telling me that makes this fear feel true.</p>
        <p><strong>How I fill it out:</strong></p>
        <ul>
            <li>I ask myself: What am I afraid will happen? What lie am I believing?</li>
            <li>I keep it short—just a few words or a simple phrase.</li>
        </ul>
        <p><strong>Examples:</strong></p>
        <ul>
            <li>"I fear losing my job because I think I'm not good enough."</li>
            <li>"I fear being alone because I believe no one will care about me."</li>
            <li>"I fear failing because I've let people down before."</li>
        </ul>
      `,
      'fear-impact': `
        <h3>How does this fear affect me?</h3>
        <p>I select one or more parts of me that this fear touches—these are my inner feelings or needs that feel threatened.</p>
        <p><strong>I choose the areas it affects:</strong></p>
        <ul>
            <li><strong>Self‑esteem:</strong> How I feel about myself, my sense of worth.</li>
            <li><strong>Pride:</strong> How I think others see or judge me.</li>
            <li><strong>Pocketbook:</strong> My sense of financial or material security.</li>
            <li><strong>Personal Relationships:</strong> Trust, closeness, and support in my friendships or family.</li>
            <li><strong>Sexual Relationships:</strong> My intimate connections and trust with others.</li>
            <li><strong>Emotional Security:</strong> My inner calm and sense of safety.</li>
            <li><strong>Ambitions:</strong> My hopes, goals, and dreams for the future.</li>
        </ul>
        <p><strong>Why this matters:</strong></p>
        <p>Choosing these helps me see which parts of me feel shaken by this fear—so I can know where I may need support or healing.</p>
      `,
      'harm-who': `
        <h3>Who did I harm?</h3>
        <p>In this field, I list <strong>everyone I've harmed</strong>, whether through big actions or small ones—by what I did or didn't do. I include anyone affected by my behavior, directly or indirectly.</p>
        <p><strong>What I do:</strong></p>
        <ul>
            <li>I write down the names of people I harmed—family, friends, partners, co-workers, acquaintances, or even people I barely know.</li>
            <li>I also include <strong>myself</strong>, recognizing that my actions or choices have often hurt me too.</li>
        </ul>
        <p><strong>Examples:</strong></p>
        <ul>
            <li>"My spouse"</li>
            <li>"My sister"</li>
            <li>"My coworker Mark"</li>
            <li>"Myself"</li>
        </ul>
      `,
      'harm-how': `
        <h3>How did I harm them?</h3>
        <p>In this field, I briefly describe <strong>what I did or failed to do</strong> that caused harm. I keep it factual and to the point—a short summary of my action or inaction.</p>
        <p><strong>How I fill it out:</strong></p>
        <ul>
            <li>I write exactly what I did, without excuses or long stories.</li>
            <li>I keep it brief—like a bullet point.</li>
            <li>I may have <strong>multiple entries for the same person</strong>, especially for those closest to me, because I may have harmed them in different ways.</li>
        </ul>
        <p><strong>Examples:</strong></p>
        <ul>
            <li>"I ignored my brother's message when he needed help."</li>
            <li>"I lied to my partner about where I was."</li>
            <li>"I took money from my roommate without asking."</li>
        </ul>
        <p>This helps me see clearly how I've impacted others and prepares me for making things right later.</p>
      `,
      'harm-character': `
        <h3>What part of my character caused this harm?</h3>
        <p>Here, I look at <strong>which parts of my character contributed to the harm I caused</strong>. I select one or more that fit. Being honest helps me see my patterns and take responsibility.</p>
        <p><strong>What each means:</strong></p>
        <ul>
            <li><strong>Controlling:</strong> I tried to force outcomes or people to do what I wanted.</li>
            <li><strong>Dishonest:</strong> I didn't tell the truth, hid things, or twisted facts.</li>
            <li><strong>Disengaged:</strong> I withdrew, avoided, or neglected people or situations.</li>
            <li><strong>Entitled:</strong> I believed I deserved special treatment or could do as I pleased.</li>
            <li><strong>Frightened:</strong> I acted out of fear, letting it drive my decisions.</li>
            <li><strong>Greedy:</strong> I wanted more than my share—of attention, things, or experiences.</li>
            <li><strong>Inconsiderate:</strong> I didn't think about how my actions would affect others.</li>
            <li><strong>Impulsive:</strong> I acted without thinking or caring about the consequences.</li>
            <li><strong>Lustful:</strong> I used others to satisfy my sexual desires without regard for them.</li>
            <li><strong>Self-pity:</strong> I felt sorry for myself and used that to justify harmful actions.</li>
            <li><strong>Selfish:</strong> I put my own wants and needs above everyone else's.</li>
            <li><strong>Willful:</strong> I pushed ahead with what I wanted, ignoring what was right or wise.</li>
        </ul>
      `,
      'harm-alternative': `
        <h3>What could I have done differently?</h3>
        <p>In this field, I reflect on <strong>how I might have handled things better</strong>—actions I could have taken that would have caused less harm or resolved the situation more responsibly.</p>
        <p><strong>How I fill it out:</strong></p>
        <ul>
            <li>I keep it simple and constructive.</li>
            <li>I focus on real, practical alternatives—what I <em>could</em> have done instead.</li>
        </ul>
        <p><strong>Examples:</strong></p>
        <ul>
            <li>"I could have listened and reached out instead of walking away."</li>
            <li>"I could have been honest about my whereabouts."</li>
            <li>"I could have asked before using my roommate's money."</li>
        </ul>
        <p>Choosing these alternatives helps me see how I can grow and change in future actions.</p>
      `
    };
    
    const content = helpTexts[fieldId] || '<h3>Help</h3><p>Help content not available.</p>';
    showHelpDialog({
      content,
      title: 'Help'
    });
  };

  const handleDownload = async () => {
    try {
      const currentData = getCurrentData();
      
      if (currentData.length === 0) {
        showAlert({
          message: 'There are no entries to download in this inventory.',
          variant: 'info'
        });
        return;
      }

      // Encrypt the inventory data
      const encryptedData = await EncryptionService.encrypt({ items: currentData });
      
      // Create download data with metadata
      const downloadData = {
        metadata: {
          exportDate: new Date().toISOString(),
          inventoryType: currentType,
          version: getCurrentVersionName(),
          itemCount: currentData.length
        },
        encryptedData: encryptedData
      };

      const jsonData = JSON.stringify(downloadData, null, 2);
      const blob = new Blob([jsonData], { type: 'application/json' });
      
      // Generate filename with date
      const date = new Date().toISOString().split('T')[0];
      const filename = `${currentType}-inventory-${date}.json`;

      // Try to use File System API for better UX (let user choose location)
      const supportsFileSystemAPI = 'showSaveFilePicker' in window;
      
      if (supportsFileSystemAPI) {
        try {
          const handle = await (window as any).showSaveFilePicker({
            suggestedName: filename,
            types: [{
              description: 'Encrypted Inventory File',
              accept: { 'application/json': ['.json'] }
            }]
          });
          const writable = await handle.createWritable();
          await writable.write(jsonData);
          await writable.close();
          
          showAlert({
            message: `Successfully downloaded ${currentData.length} encrypted ${currentType} entries.`,
            variant: 'success'
          });
          return;
        } catch (error) {
          // User cancelled or API failed, fall back to regular download
          if (error instanceof Error && error.name === 'AbortError') {
            return; // User cancelled, don't show error
          }
        }
      }

      // Fallback: Regular download
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      showAlert({
        message: `Successfully downloaded ${currentData.length} encrypted ${currentType} entries.`,
        variant: 'success'
      });
    } catch (error) {
      console.error('Download failed:', error);
      showAlert({
        message: 'Failed to download inventory. Please try again.',
        variant: 'danger'
      });
    }
  };

  const handleCompletionCheckbox = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    const currentData = getCurrentData();
    const total = currentData.length;
    const complete = currentData.filter((item: any) => isComplete(currentType, item)).length;
    
    
    
    // If no entries, don't allow checking
    if (total === 0) {
      return;
    }
    
    // If there are incomplete entries, show warning
    if (complete < total) {
      showAlert({
        message: 'There are entries in your inventory that are incomplete.',
        variant: 'warning'
      });
      return;
    }
    
    // If all entries are complete, allow the checkbox change
    setCompletionStatus(prev => {
      const newStatus = {
        ...prev,
        [currentType]: checked
      };
      
      return newStatus;
    });
    
    // Save completion status as part of the inventory data
    if (user) {
      try {

        
        // Update the completion status state
        setCompletionStatus(prev => ({
          ...prev,
          [currentType]: checked
        }));
        
        // Get current inventory data
        const currentData = getCurrentData();
        
        // Save the inventory data to API (without completion status)
        const versionName = getCurrentVersionName();
        await InventoryService.saveInventory(currentType, versionName, currentData);
        
        // Save completion status using the dedicated endpoint
        await InventoryService.saveInventoryCompletion(currentType, versionName, checked);
        
        // Update the local data state
        setData(prev => ({
          ...prev,
          [currentType]: currentData
        }));
        
        // Notify parent to update counters (completion affects status)
        if (onInventoryUpdate) {
          onInventoryUpdate(currentType);
        }
        
        // Don't reload from API - local state is already updated
        // Reloading causes race condition
      } catch (error) {
        console.error('Error saving completion status:', error);
        // Revert the state change if save fails
        setCompletionStatus(prev => ({
          ...prev,
          [currentType]: !checked
        }));
      }
    }
  };

  const handleUpload = async () => {
    try {
      let file: File;
      
      // Try to use File System API for better UX
      const supportsFileSystemAPI = 'showOpenFilePicker' in window;
      
      if (supportsFileSystemAPI) {
        try {
          const [fileHandle] = await (window as any).showOpenFilePicker({
            types: [{
              description: 'Encrypted Inventory Files',
              accept: { 'application/json': ['.json'] }
            }],
            multiple: false
          });
          file = await fileHandle.getFile();
        } catch (error) {
          // User cancelled
          if (error instanceof Error && error.name === 'AbortError') {
            return;
          }
          throw error;
        }
      } else {
        // Fallback: Traditional file input
        file = await new Promise<File>((resolve, reject) => {
          const input = document.createElement('input');
          input.type = 'file';
          input.accept = '.json';
          input.onchange = (e) => {
            const selectedFile = (e.target as HTMLInputElement).files?.[0];
            if (selectedFile) {
              resolve(selectedFile);
            } else {
              reject(new Error('No file selected'));
            }
          };
          input.click();
        });
      }

      // Check file extension
      if (!file.name.toLowerCase().endsWith('.json')) {
        setImportStatus('Error: Please select a JSON file exported from Recovery Commons.');
        setTimeout(() => setImportStatus(''), 5000);
        return;
      }

      setImportStatus('Reading and decrypting file...');

      const text = await file.text();
      const uploadedData = JSON.parse(text);
      
      // Validate structure
      if (!uploadedData.metadata || !uploadedData.encryptedData) {
        throw new Error('Invalid file format. Please use a file exported from Recovery Commons.');
      }

      // Check if the inventory type matches
      if (uploadedData.metadata.inventoryType !== currentType) {
        throw new Error(`This file contains ${uploadedData.metadata.inventoryType} data, but you're trying to upload it to ${currentType}. Please switch to the correct inventory tab.`);
      }

      // Decrypt the data
      const decryptedData = await EncryptionService.decrypt(uploadedData.encryptedData);
      const parsedData = decryptedData.items || [];

      if (parsedData.length === 0) {
        throw new Error('No valid data found in the file.');
      }

      // Replace current inventory data with uploaded data
      const versionName = getCurrentVersionName();
      
      setData(prev => ({
        ...prev,
        [currentType]: parsedData
      }));

      // Save to API
      if (user) {
        try {
          await InventoryService.saveInventory(currentType, versionName, parsedData);
          
          // Don't reload from API - local state is already updated
          // Reloading causes race condition
        } catch (error) {
          console.error('Error saving to API:', error);
          throw new Error('Failed to save imported data to your account.');
        }
      }
      
      setImportStatus(`Successfully uploaded ${parsedData.length} entries.`);
      setTimeout(() => setImportStatus(''), 3000);
      
    } catch (error) {
      console.error('Upload failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred while uploading the file.';
      setImportStatus(`Upload failed: ${errorMessage}`);
      setTimeout(() => setImportStatus(''), 5000);
    }
  };



  if (!isOpen) return null;

  // Entry label and placeholder
  let entryLabel = '';
  let entryPlaceholder = '';
  let myLabel = '';
  let welcomeText = '';
  if (currentType === 'resentments') {
    entryLabel = 'Who do I resent?';
    entryPlaceholder = 'e.g., My sister Julie, My boss, The government...';
    myLabel = 'My Resentments';
    welcomeText = 'Start by adding your first resentment above';
  } else if (currentType === 'fears') {
    entryLabel = 'What do I fear?';
    entryPlaceholder = 'e.g., Fear of rejection, Fear of failure...';
    myLabel = 'My Fears';
    welcomeText = 'Start by adding your first fear above';
  } else if (currentType === 'harms') {
    entryLabel = 'Who did I harm?';
    entryPlaceholder = 'e.g., My wife, My children, My friend John...';
    myLabel = 'My Harms Done';
    welcomeText = 'Start by adding your first harm above';
  }

  const showWelcome = getCurrentData().length === 0;

  // Share button helpers
  const hasAnyInventoryData = (): boolean => {
    const resentments = (data['resentments'] || []).filter(item => item._type !== 'completion_status');
    const fears = (data['fears'] || []).filter(item => item._type !== 'completion_status');
    const harms = (data['harms'] || []).filter(item => item._type !== 'completion_status');
    
    return resentments.length > 0 || fears.length > 0 || harms.length > 0;
  };

  const getAllInventoryCounts = () => {
    const resentments = (data['resentments'] || []).filter(item => item._type !== 'completion_status');
    const fears = (data['fears'] || []).filter(item => item._type !== 'completion_status');
    const harms = (data['harms'] || []).filter(item => item._type !== 'completion_status');
    
    return {
      resentments: resentments.length,
      fears: fears.length,
      harms: harms.length
    };
  };

  const handleShareClick = () => {
    if (!hasAnyInventoryData()) {
      showAlert({
        message: 'Add inventory entries before sharing',
        variant: 'info'
      });
      return;
    }

    const counts = getAllInventoryCounts();
    
    // Build the message with proper formatting
    const inventoryLines = [];
    if (counts.resentments > 0) {
      inventoryLines.push(`• Resentments (${counts.resentments} ${counts.resentments === 1 ? 'entry' : 'entries'})`);
    }
    if (counts.fears > 0) {
      inventoryLines.push(`• Fears (${counts.fears} ${counts.fears === 1 ? 'entry' : 'entries'})`);
    }
    if (counts.harms > 0) {
      inventoryLines.push(`• Harms Done (${counts.harms} ${counts.harms === 1 ? 'entry' : 'entries'})`);
    }
    
    const message = `This will create a summary of your inventory entries that you can view, print, and/or share:\n\n${inventoryLines.join('\n')}`;
    
    showConfirmDialog({
      message: message,
      onConfirm: () => {
        // Keep inventory modal open, ShareModal appears on top (z-index: 1100 > 1000)
        openShareModal({ autoGenerate: 'step-four-inventories' });
      },
      confirmText: 'Continue'
    });
  };

  const shareButtonEnabled = hasAnyInventoryData();

  return (
    <div className={styles.modalOverlay} onClick={handleClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Step Four Inventories</h2>
          <button
            className={styles.shareButton}
            onClick={handleShareClick}
            disabled={!shareButtonEnabled}
            title={shareButtonEnabled 
              ? "Share your Step Four inventories" 
              : "Add inventory entries before sharing"}
          >
            <span className={styles.shareButtonText}>Share</span>
            <span className={styles.shareButtonIcon}>📤</span>
          </button>
          <button className={styles.closeButton} onClick={handleClose}>
            ×
          </button>
        </div>

        <div className={styles.modalBody}>
          {/* Tabs */}
          <div className={styles.inventoryTabs}>
            {INVENTORY_TYPES.map((t) => {
              // Use inventory statuses from props instead of local data
              const status = inventoryStatuses?.find(s => s.type === t.key);
              const total = status?.total || 0;
              const isCompleted = status?.isCompleted || false;
              
              // Determine status text and color
              let statusText = '';
              let bulletColor = '';
              
              if (total === 0) {
                statusText = 'not started';
                bulletColor = '#9ca3af'; // gray
              } else if (isCompleted) {
                statusText = `completed (${total})`;
                bulletColor = '#10b981'; // green
              } else {
                statusText = `in progress (${total})`;
                bulletColor = '#fbbf24'; // yellow
              }
              
              return (
                <button
                  key={t.key}
                  onClick={() => handleTab(t.key)}
                  className={`${styles.tabBtn} ${currentType === t.key ? styles.active : ''}`}
                >
                  <span className={styles.tabLabel}>{t.label}</span>
                  <span className={styles.tabBullet} style={{ color: bulletColor }}>•</span>
                  <span className={styles.tabStatus}>{statusText}</span>
                </button>
              );
            })}
          </div>

          {/* Header Section */}
          <div className={styles.inventoryHeader}>
            <h3 className={styles.inventoryHeaderTitle}>
              {INVENTORY_TYPES.find(t => t.key === currentType)?.label} Inventory
              <button 
                type="button" 
                className={styles.helpBtn} 
                onClick={() => openHelp('inventory')}
                title="Get help with inventories"
                tabIndex={-1}
              >
                ?
              </button>
            </h3>
            
            {/* Centered Completion Checkbox */}
            <div className={styles.completionCheckboxCentered}>
              <label className={styles.completionLabel}>
                <input
                  type="checkbox"
                  checked={completionStatus[currentType] || false}
                  onChange={handleCompletionCheckbox}
                  disabled={getCurrentData().length === 0}
                  className={styles.completionInput}
                />
                <span className={styles.completionText}>Inventory Completed</span>
              </label>
            </div>
          </div>
          
          {importStatus && (
            <div className={`${styles.importStatus} ${importStatus.includes('failed') ? styles.error : styles.success}`}>
              {importStatus}
            </div>
          )}





          {/* Entry Add and Inventory List - always show */}
          {/* Entry Add */}
              <form className={styles.quickForm} onSubmit={handleEntryAdd}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    {entryLabel} 
                    <button 
                      type="button" 
                      className={styles.helpBtn} 
                      onClick={() => openHelp(`${currentType === 'resentments' ? 'resentment' : currentType === 'fears' ? 'fear' : 'harm'}-${currentType === 'resentments' ? 'who' : currentType === 'fears' ? 'what' : 'who'}`)}
                      title="Get help with this field"
                      tabIndex={-1}
                    >
                      ?
                    </button>
                  </label>
                  <div className={styles.quickInputGroup}>
                    <input
                      type="text"
                      value={entryInput}
                      onChange={e => setEntryInput(e.target.value)}
                      placeholder={entryPlaceholder}
                      required
                      className={styles.quickInput}
                    />
                    <button type="submit" className={styles.btnSuccess}>Add to List</button>
                  </div>
                </div>
              </form>

              {/* Welcome Screen */}
              {showWelcome && (
                <div className={styles.welcomeScreen}>
                  <p>{welcomeText}</p>
                </div>
              )}

              {/* Inventory List */}
              {(!showWelcome && getCurrentData().length > 0) && (
                <div className={styles.inventoryList}>
                  <h4 className={styles.inventoryTitle}>
                    {myLabel}
                    <span className={styles.inventoryCount}>
                      ({getInventoryCounts().total} total, {getInventoryCounts().complete} completed)
                    </span>
                  </h4>
                  <ul className={styles.inventoryItems}>
                    {getCurrentData().map((item: any, idx: number) => {
                      const isExpanded = expandedIndex === idx;
                      let mainLabel = '';
                      if (currentType === 'resentments') mainLabel = item.who;
                      if (currentType === 'fears') mainLabel = item.fear;
                      if (currentType === 'harms') mainLabel = item.whoHarmed;
                      const complete = isComplete(currentType, item);
                      
                      return (
                        <li
                          key={idx}
                          className={`${styles.inventoryItem} ${complete ? styles.completeEntry : styles.incompleteEntry}`}
                        >
                          <div className={styles.itemSummary} onClick={() => handleExpand(idx)}>
                            <div className={styles.itemText}>
                              <span className={styles.expandIcon}>{isExpanded ? '▲' : '▼'}</span>
                              <span className={styles.itemLabel}>{mainLabel}</span>
                              {savingEntry === idx && (
                                <span className={styles.savingText}>Saving...</span>
                              )}
                              {savedEntry === idx && (
                                <span className={styles.savedText}>✓ Saved</span>
                              )}
                              {saveError === idx && (
                                <span className={styles.saveErrorText}>⚠ Save failed</span>
                              )}
                              {savingEntry !== idx && savedEntry !== idx && saveError !== idx && (
                                <>
                                  {complete && (
                                    <span className={styles.completeIcon}>✓</span>
                                  )}
                                  {!complete && (
                                    <span className={styles.incompleteText}>click to see/add details</span>
                                  )}
                                </>
                              )}
                            </div>
                            <div className={styles.itemControls}>
                              <button 
                                onClick={e => { e.stopPropagation(); handleDelete(idx); }} 
                                className={styles.deleteButton}
                                title="Delete"
                              >
                                ×
                              </button>
                            </div>
                          </div>
                          
                          {/* Edit Form (inline, shown directly when expanded) */}
                          {isExpanded && (
                            <div className={styles.inventoryForm}>
                                
                                {currentType === 'resentments' && (
                                  <>
                                    <div className={styles.formGroup}>
                                      <label>
                                        Who do I resent? 
                                        <button 
                                          type="button" 
                                          className={styles.helpBtn} 
                                          onClick={() => openHelp('resentment-who')}
                                          title="Get help with this field"
                                          tabIndex={-1}
                                        >
                                          ?
                                        </button>
                                      </label>
                                      <input 
                                        name="who" 
                                        placeholder="Who" 
                                        value={form?.who || ''} 
                                        onChange={handleFormChange}
                                        onBlur={handleFieldBlur}
                                        required 
                                      />
                                    </div>
                                    <div className={styles.formGroup}>
                                      <label>
                                        Why do I resent them? 
                                        <button 
                                          type="button" 
                                          className={styles.helpBtn} 
                                          onClick={() => openHelp('resentment-why')}
                                          title="Get help with this field"
                                          tabIndex={-1}
                                        >
                                          ?
                                        </button>
                                      </label>
                                      <textarea 
                                        name="why" 
                                        placeholder="What happened? What did they do?" 
                                        value={form?.why || ''} 
                                        onChange={handleFormChange}
                                        onBlur={handleFieldBlur}
                                        rows={3} 
                                      />
                                    </div>
                                    <div className={styles.formGroup}>
                                      <label>
                                        How did that make me feel? 
                                        <button 
                                          type="button" 
                                          className={styles.helpBtn} 
                                          onClick={() => openHelp('resentment-feeling')}
                                          title="Get help with this field"
                                          tabIndex={-1}
                                        >
                                          ?
                                        </button>
                                      </label>
                                      <textarea 
                                        name="feeling" 
                                        placeholder="e.g., angry, hurt, betrayed, embarrassed..." 
                                        value={form?.feeling || ''} 
                                        onChange={handleFormChange}
                                        onBlur={handleFieldBlur}
                                        rows={2} 
                                      />
                                    </div>
                                    <div className={styles.formGroup}>
                                      <label>
                                        What was lost, taken, or threatened inside me? 
                                        <button 
                                          type="button" 
                                          className={styles.helpBtn} 
                                          onClick={() => openHelp('resentment-impact')}
                                          title="Get help with this field"
                                          tabIndex={-1}
                                        >
                                          ?
                                        </button>
                                      </label>
                                      <div className={styles.checkboxGroup} onBlur={handleFieldBlur}>
                                        {IMPACT_OPTIONS.map(opt => (
                                          <label key={opt.value} className={styles.checkboxItem}>
                                            <input
                                              type="checkbox"
                                              name="impacts"
                                              value={opt.value}
                                              checked={form?.impacts?.includes(opt.value) || false}
                                              onChange={e => handleCheckboxChange(e, 'impacts')}
                                            />
                                            {opt.label}
                                          </label>
                                        ))}
                                      </div>
                                    </div>
                                    <div className={styles.formGroup}>
                                      <label>
                                        What was my part in this resentment? 
                                        <button 
                                          type="button" 
                                          className={styles.helpBtn} 
                                          onClick={() => openHelp('resentment-part')}
                                          title="Get help with this field"
                                          tabIndex={-1}
                                        >
                                          ?
                                        </button>
                                      </label>
                                      <textarea 
                                        name="myPart" 
                                        placeholder="Be honest about your role, even if it's small..." 
                                        value={form?.myPart || ''} 
                                        onChange={handleFormChange}
                                        onBlur={handleFieldBlur}
                                        rows={3} 
                                      />
                                    </div>
                                  </>
                                )}
                                
                                {currentType === 'fears' && (
                                  <>
                                    <div className={styles.formGroup}>
                                      <label>
                                        What do I fear? 
                                        <button 
                                          type="button" 
                                          className={styles.helpBtn} 
                                          onClick={() => openHelp('fear-what')}
                                          title="Get help with this field"
                                          tabIndex={-1}
                                        >
                                          ?
                                        </button>
                                      </label>
                                      <input 
                                        name="fear" 
                                        placeholder="Fear" 
                                        value={form?.fear || ''} 
                                        onChange={handleFormChange}
                                        onBlur={handleFieldBlur}
                                        required 
                                      />
                                    </div>
                                    <div className={styles.formGroup}>
                                      <label>
                                        Why do I have this fear? 
                                        <button 
                                          type="button" 
                                          className={styles.helpBtn} 
                                          onClick={() => openHelp('fear-why')}
                                          title="Get help with this field"
                                          tabIndex={-1}
                                        >
                                          ?
                                        </button>
                                      </label>
                                      <textarea 
                                        name="why" 
                                        placeholder="What's behind this fear? What caused it?" 
                                        value={form?.why || ''} 
                                        onChange={handleFormChange}
                                        onBlur={handleFieldBlur}
                                        rows={3} 
                                      />
                                    </div>
                                    <div className={styles.formGroup}>
                                      <label>
                                        How does this fear affect me? 
                                        <button 
                                          type="button" 
                                          className={styles.helpBtn} 
                                          onClick={() => openHelp('fear-impact')}
                                          title="Get help with this field"
                                          tabIndex={-1}
                                        >
                                          ?
                                        </button>
                                      </label>
                                      <div className={styles.checkboxGroup} onBlur={handleFieldBlur}>
                                        {IMPACT_OPTIONS.map(opt => (
                                          <label key={opt.value} className={styles.checkboxItem}>
                                            <input
                                              type="checkbox"
                                              name="impacts"
                                              value={opt.value}
                                              checked={form?.impacts?.includes(opt.value) || false}
                                              onChange={e => handleCheckboxChange(e, 'impacts')}
                                            />
                                            {opt.label}
                                          </label>
                                        ))}
                                      </div>
                                    </div>
                                  </>
                                )}
                                
                                {currentType === 'harms' && (
                                  <>
                                    <div className={styles.formGroup}>
                                      <label>
                                        Who did I harm? 
                                        <button 
                                          type="button" 
                                          className={styles.helpBtn} 
                                          onClick={() => openHelp('harm-who')}
                                          title="Get help with this field"
                                          tabIndex={-1}
                                        >
                                          ?
                                        </button>
                                      </label>
                                      <input 
                                        name="whoHarmed" 
                                        placeholder="Who Harmed" 
                                        value={form?.whoHarmed || ''} 
                                        onChange={handleFormChange}
                                        onBlur={handleFieldBlur}
                                        required 
                                      />
                                    </div>
                                    <div className={styles.formGroup}>
                                      <label>
                                        How did I harm them? 
                                        <button 
                                          type="button" 
                                          className={styles.helpBtn} 
                                          onClick={() => openHelp('harm-how')}
                                          title="Get help with this field"
                                          tabIndex={-1}
                                        >
                                          ?
                                        </button>
                                      </label>
                                      <textarea 
                                        name="how" 
                                        placeholder="What did you do that hurt them?" 
                                        value={form?.how || ''} 
                                        onChange={handleFormChange}
                                        onBlur={handleFieldBlur}
                                        rows={3} 
                                      />
                                    </div>

                                    <div className={styles.formGroup}>
                                      <label>
                                        What part of my character caused this harm? 
                                        <button 
                                          type="button" 
                                          className={styles.helpBtn} 
                                          onClick={() => openHelp('harm-character')}
                                          title="Get help with this field"
                                          tabIndex={-1}
                                        >
                                          ?
                                        </button>
                                      </label>
                                      <div className={styles.checkboxGroup} onBlur={handleFieldBlur}>
                                        {DEFECT_OPTIONS.map(opt => (
                                          <label key={opt.value} className={styles.checkboxItem}>
                                            <input
                                              type="checkbox"
                                              name="defects"
                                              value={opt.value}
                                              checked={form?.defects?.includes(opt.value) || false}
                                              onChange={e => handleCheckboxChange(e, 'defects')}
                                            />
                                            {opt.label}
                                          </label>
                                        ))}
                                      </div>
                                    </div>
                                    <div className={styles.formGroup}>
                                      <label>
                                        What could I have done differently? 
                                        <button 
                                          type="button" 
                                          className={styles.helpBtn} 
                                          onClick={() => openHelp('harm-alternative')}
                                          title="Get help with this field"
                                          tabIndex={-1}
                                        >
                                          ?
                                        </button>
                                      </label>
                                      <textarea 
                                        name="alternative" 
                                        placeholder="What would have been a better choice?" 
                                        value={form?.alternative || ''} 
                                        onChange={handleFormChange}
                                        onBlur={handleFieldBlur}
                                        rows={3} 
                                      />
                                    </div>
                                  </>
                                )}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
        </div>
      </div>
    </div>
  );
};

export default InventoryModal; 